/**
 * DocumentUploadModal — drag-and-drop upload, modal-everywhere style.
 *
 * Steps:
 *   1. User drops or picks a file.
 *   2. Form auto-detects MIME / size; user picks docCode from the catalog
 *      and (optionally) writes a note.
 *   3. Submit → calls `apiClient.retail.documents.upload`. On success the modal
 *      closes and the parent's onUploaded() is called so it can refresh the
 *      tile grid + emit a session event for DocumentRequirements consumers.
 *
 * When the upload route is unavailable (501) we surface the friendly fallback
 * message rather than throwing — prevents the FSR seeing raw error JSON.
 *
 * Auto-fulfill wiring: emits a `document.uploaded` SessionEvent that
 * DocumentRequirements consumers (RAO ReviewStep / RDL CIF view) listen to so
 * the slot ticks green without a manual refresh.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload as UploadIcon, X } from '@retail-ops/panel-shell/fi';
import type { DocumentCategory, DocumentRef, DocumentType } from '@retail-ops/api-client';
import { ModalShell, Button, Banner } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { DocumentsClient } from '../services/documentsClient';
import { CATEGORY_LABEL } from '@/hooks/useDocuments';

export interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploaded?: (doc: DocumentRef | null, meta: { docCode: string; cifId: string; cartId?: string }) => void;
  /** Default CIF id (when invoked from a CIF-scoped view). */
  defaultCifId?: string;
  /** Default cart id (when invoked from a cart-scoped view). */
  defaultCartId?: string;
  /** Default category — defaults to fsr_upload. */
  defaultCategory?: DocumentCategory;
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.tiff,.tif,.heic';
const MAX_BYTES = 16 * 1024 * 1024;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file.'));
        return;
      }
      // Result is `data:<mime>;base64,<bytes>` — strip the prefix.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error('Read error.'));
    reader.readAsDataURL(file);
  });
}

export function DocumentUploadModal({
  open,
  onClose,
  onUploaded,
  defaultCifId = '',
  defaultCartId,
  defaultCategory = 'fsr_upload',
}: DocumentUploadModalProps): JSX.Element {
  const apiClient = useApiClient();
  const docsClient = useMemo(() => new DocumentsClient(apiClient), [apiClient]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [cifId, setCifId] = useState<string>(defaultCifId);
  const [cartId, setCartId] = useState<string>(defaultCartId ?? '');
  const [category, setCategory] = useState<DocumentCategory>(defaultCategory);
  const [docCode, setDocCode] = useState<string>('OTHER');
  const [notes, setNotes] = useState<string>('');
  const [docTypes, setDocTypes] = useState<ReadonlyArray<DocumentType>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  // Reset state when modal opens.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setCifId(defaultCifId);
    setCartId(defaultCartId ?? '');
    setCategory(defaultCategory);
    setDocCode('OTHER');
    setNotes('');
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  }, [open, defaultCifId, defaultCartId, defaultCategory]);

  // Load document type catalog when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const ctrl = new AbortController();
    void (async (): Promise<void> => {
      const types = await docsClient.types(ctrl.signal);
      if (cancelled) return;
      setDocTypes(types);
    })();
    return (): void => {
      cancelled = true;
      ctrl.abort();
    };
  }, [open, docsClient]);

  const canSubmit = !!file && cifId.trim().length > 0 && !submitting;

  const handleFileChosen = (f: File | null): void => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError(`File is larger than the 16 MB upload limit (${(f.size / 1024 / 1024).toFixed(1)} MB).`);
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChosen(e.dataTransfer.files[0] ?? null);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);

    try {
      const bytesBase64 = await readFileAsBase64(file);
      const result = await docsClient.upload({
        cifId: cifId.trim(),
        ...(cartId.trim() ? { cartId: cartId.trim() } : {}),
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        bytesBase64,
        category,
        docCode,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });

      if (!result.success) {
        setError(
          result.error
            ? `Upload failed: ${result.error}`
            : 'Upload failed. The document service may be offline; try again shortly.',
        );
        setSubmitting(false);
        return;
      }

      setSuccess(`Uploaded ${file.name}.`);
      // Fire DocumentRequirements auto-fulfill event — fire-and-forget.
      try {
        if (apiClient) {
          await apiClient.retail.events.write({
            eventType: 'document.uploaded',
            eventCategory: 'cif-account',
            severity: 'info',
            ...(cartId.trim() ? { cartId: cartId.trim() } : {}),
            payloadJson: JSON.stringify({
              cifId: cifId.trim(),
              ...(cartId.trim() ? { cartId: cartId.trim() } : {}),
              docCode,
              category,
              fileName: file.name,
              documentId: result.documentId,
            }),
          });
        }
      } catch {
        // Non-fatal — the event log is advisory.
      }

      onUploaded?.(null, {
        docCode,
        cifId: cifId.trim(),
        ...(cartId.trim() ? { cartId: cartId.trim() } : {}),
      });

      // Briefly show success then close.
      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      setError(msg);
      setSubmitting(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
      <Button variant="ghost" size="sm" startIcon={<X size={14} />} onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        startIcon={<UploadIcon size={14} />}
        type="submit"
        form="rdl-upload-form"
        disabled={!canSubmit}
      >
        {submitting ? 'Uploading…' : 'Upload'}
      </Button>
    </div>
  );

  return (
    <ModalShell
      open={open}
      onClose={(): void => {
        if (!submitting) onClose();
      }}
      title="Upload document"
      size="md"
      footer={footer}
      ariaLabel="Upload document"
    >
      <form id="rdl-upload-form" onSubmit={(e) => void handleSubmit(e)}>
        <div
          className={`rdl-drop ${dragActive ? 'is-active' : ''}`.trim()}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Choose a file to upload"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <UploadIcon size={28} aria-hidden style={{ color: 'var(--ink-500)' }} />
          <div className="rdl-drop__title">
            {file ? file.name : 'Drag a file here, or click to browse'}
          </div>
          <div className="rdl-drop__sub">
            PDF, JPG, PNG, TIFF, or HEIC · up to 16 MB
          </div>
          {file && (
            <div className="rdl-drop__file">
              {(file.size / 1024).toFixed(0)} KB · {file.type || 'unknown type'}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', marginTop: 'var(--sp-5)' }}>
          <div className="rdl-form-grid">
            <div>
              <label className="rdl-field-label" htmlFor="rdl-upload-cif">CIF id</label>
              <input
                id="rdl-upload-cif"
                type="text"
                className="rdl-field"
                value={cifId}
                onChange={(e) => setCifId(e.target.value)}
                placeholder="e.g. 0001234"
                required
              />
            </div>
            <div>
              <label className="rdl-field-label" htmlFor="rdl-upload-cart">Cart id (optional)</label>
              <input
                id="rdl-upload-cart"
                type="text"
                className="rdl-field"
                value={cartId}
                onChange={(e) => setCartId(e.target.value)}
                placeholder="leave blank if not in a cart"
              />
            </div>
          </div>

          <div className="rdl-form-grid">
            <div>
              <label className="rdl-field-label" htmlFor="rdl-upload-doctype">Document type</label>
              <select
                id="rdl-upload-doctype"
                className="rdl-field"
                value={docCode}
                onChange={(e) => setDocCode(e.target.value)}
              >
                {docTypes.map((t) => (
                  <option key={t.docCode} value={t.docCode}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="rdl-field-label" htmlFor="rdl-upload-category">Category</label>
              <select
                id="rdl-upload-category"
                className="rdl-field"
                value={category}
                onChange={(e) => setCategory(e.target.value as DocumentCategory)}
              >
                <option value="fsr_upload">{CATEGORY_LABEL.fsr_upload}</option>
                <option value="customer_upload">{CATEGORY_LABEL.customer_upload}</option>
                <option value="signed_pdf">{CATEGORY_LABEL.signed_pdf}</option>
                <option value="rao_output">{CATEGORY_LABEL.rao_output}</option>
                <option value="bank_generated">{CATEGORY_LABEL.bank_generated}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="rdl-field-label" htmlFor="rdl-upload-notes">Note (optional)</label>
            <textarea
              id="rdl-upload-notes"
              className="rdl-field rdl-field--textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context visible in the audit trail."
              rows={3}
            />
          </div>

          {error && <Banner tone="err" title="Upload problem">{error}</Banner>}
          {success && <Banner tone="ok" title="Done">{success}</Banner>}
        </div>
      </form>
    </ModalShell>
  );
}
